// src/constants/performance.js
// Constantes do módulo de Performance e Ranking de Colaboradores

import { getOptionsFromMap } from './common';

/**
 * Grupos de métricas para o ranking de colaboradores.
 * Cada cargo (role) deve ter um metricGroup associado.
 * 
 * Glossário de consistência com o resto do projeto:
 * - "Colaborador" = employee (termo genérico na UI, menu, formulários)
 * - "Professor"   = teacher  (quem ministra aulas, aparece na Grade e Turmas)
 * - "Consultor"   = consultant (quem faz vendas, CRM, experimentais, carteira)
 * - "Clientes"    = member (alunos na UI — nunca "aluno" no código/labels)
 */
export const METRIC_GROUPS = {
    PROFESSOR: 'professor',
    CONSULTOR: 'consultor',
    GERENTE: 'gerente',
};

export const METRIC_GROUP_LABELS = {
    [METRIC_GROUPS.PROFESSOR]: 'Professor',
    [METRIC_GROUPS.CONSULTOR]: 'Consultor(a)',
    [METRIC_GROUPS.GERENTE]: 'Academia',
};

export const METRIC_GROUP_DESCRIPTIONS = {
    [METRIC_GROUPS.PROFESSOR]: 'Métricas de aula, frequência e conversão de experimentais',
    [METRIC_GROUPS.CONSULTOR]: 'Métricas de vendas, renovações, faturamento e CRM',
    [METRIC_GROUPS.GERENTE]: 'Métricas macro de receita, retenção e crescimento',
};

export const METRIC_GROUP_OPTIONS = getOptionsFromMap(METRIC_GROUPS, METRIC_GROUP_LABELS);

export const METRIC_GROUP_ICONS = {
    [METRIC_GROUPS.PROFESSOR]: 'mdi-school',
    [METRIC_GROUPS.CONSULTOR]: 'mdi-handshake',
    [METRIC_GROUPS.GERENTE]: 'mdi-shield-crown',
};

/**
 * Pesos padrão de cada métrica por grupo.
 * Chave = ID da métrica, Valor = peso (0 a 1, total = 1.0 por grupo).
 */
export const DEFAULT_WEIGHTS = {
    [METRIC_GROUPS.PROFESSOR]: {
        trialConversion: 0.15,
        attendanceRate: 0.15,
        assiduidade: 0.10,
        ocupacaoTurmas: 0.25,
        carteiraHealth: 0.25,
        evaluationCoverage: 0.10,
    },
    [METRIC_GROUPS.CONSULTOR]: {
        revenue: 0.25,             // C4 — Faturamento Gerado
        renewalRate: 0.20,         // C2 — Taxa de Renovação (%)
        conversionRate: 0.20,      // C3 — Taxa Conversão de Leads
        salesNew: 0.15,            // C1 — Vendas Novas
        tasksOnTime: 0.15,         // C7 — Tarefas no Prazo (%)
        trialScheduled: 0.05,      // C5 — Experimentais Agendadas
    },
    [METRIC_GROUPS.GERENTE]: {
        revenue: 0.20,             // G1 — Receita Total
        activeMembers: 0.10,       // G9 — Alunos Ativos
        salesCount: 0.10,          // G6 — Quantidade de Vendas
        ticketMedio: 0.05,         // G8 — Ticket Médio
        cancellations: 0.15,       // G7 — Quantidade de Cancelamentos (inverso)
        churnRate: 0.10,           // G2 — Taxa de Churn (inverso)
        renewalRate: 0.05,         // G10 — Taxa de Renovação (%)
        crescimentoLiquido: 0.10,  // G3 — Crescimento Líquido
        ocupacaoTurmas: 0.15,      // G4 — Lotação
    },
};

/**
 * Multiplicadores por tipo de venda (saleCategory) para cálculo de faturamento ponderado.
 * Renovação vale mais porque reter um cliente é mais valioso que captar um novo (LTV).
 */
export const SALE_CATEGORY_MULTIPLIERS = {
    NEW: 1.0,
    RENEWAL: 1.2,
    UPGRADE: 1.1,
};

/**
 * Labels amigáveis das métricas para exibição na UI (breakdown, radar chart).
 */
export const METRIC_LABELS = {
    // Professor (Colaborador Responsável pelas turmas)
    trialConversion: 'Conversão Experimental',
    attendanceRate: 'Presença dos Clientes',
    assiduidade: 'Assiduidade',
    carteiraHealth: 'Saúde da Carteira',
    evaluationCoverage: 'Cobertura de Avaliações',

    // Consultor(a) (CRM e Vendas)
    revenue: 'Faturamento',
    revenueWeighted: 'Faturamento Ponderado',
    salesNew: 'Vendas Novas',
    salesRenewal: 'Renovações',
    renewalRate: 'Taxa de Renovação',
    conversionRate: 'Conversão de Leads',
    trialScheduled: 'Experimentais Agendadas',
    trialNoShowRate: 'No-Show Experimentais',
    tasksOnTime: 'Tarefas no Prazo',

    // Academia (Visão macro do negócio)
    activeMembers: 'Alunos Ativos',
    salesCount: 'Vendas Totais',
    ticketMedio: 'Ticket Médio',
    cancellations: 'Cancelamentos',
    crescimentoLiquido: 'Crescimento Líquido',
    churnRate: 'Taxa de Churn',
    ocupacaoTurmas: 'Lotação (Ocupação)',
};

/**
 * Unidade de medida de cada métrica.
 * Usado na página de Metas para exibir o sufixo correto no input.
 */
export const METRIC_UNITS = {
    trialConversion: '%',
    attendanceRate: '%',
    assiduidade: '%',
    carteiraHealth: '%',
    ocupacaoTurmas: '%',
    conversionRate: '%',
    churnRate: '%',
    trialNoShowRate: '%',
    tasksOnTime: '%',
    renewalRate: '%',
    evaluationCoverage: '%',
    revenue: 'R$',
    revenueWeighted: 'R$',
    salesNew: 'vendas',
    salesRenewal: 'renovações',
    trialScheduled: 'agendamentos',
    ticketMedio: 'R$',
    activeMembers: 'alunos',
    crescimentoLiquido: 'clientes',
    salesCount: 'vendas',
    cancellations: 'cancelamentos',
};

/**
 * Descrições curtas de cada métrica para orientar o gestor na página de Metas.
 */
export const METRIC_DESCRIPTIONS = {
    trialConversion: 'Porcentagem de aulas experimentais convertidas em matrícula',
    attendanceRate: 'Frequência dos clientes nas aulas do professor',
    assiduidade: 'Presença do próprio professor nas aulas que ele é titular',
    carteiraHealth: 'Saúde da carteira: combina retenção de clientes (60%) com crescimento líquido das turmas (40%)',
    ocupacaoTurmas: 'Porcentagem de vagas ocupadas nas turmas calibrada por volume da sala',
    evaluationCoverage: 'Porcentagem de alunos avaliados em relação ao volume de alunos do professor',
    revenue: 'Faturamento bruto gerado no período',
    salesNew: 'Quantidade de novas vendas (primeira compra)',
    salesRenewal: 'Quantidade de renovações de plano',
    renewalRate: 'Porcentagem de contratos renovados dos clientes do consultor que venceram no mês',
    conversionRate: 'Porcentagem de leads convertidos em clientes pagantes',
    trialScheduled: 'Quantidade de aulas experimentais agendadas',
    tasksOnTime: 'Porcentagem de tarefas CRM concluídas antes ou na data de vencimento',
    ticketMedio: 'Valor médio por venda realizada',
    activeMembers: 'Número total de alunos com status ativo na academia',
    crescimentoLiquido: 'Saldo líquido de novos clientes menos cancelamentos',
    churnRate: 'Porcentagem de clientes que cancelaram (quanto menor, melhor)',
    salesCount: 'Quantidade de vendas totais aprovadas da academia',
    cancellations: 'Quantidade absoluta de alunos perdidos (cancelamentos/inadimplência)',
};

/**
 * Métricas inversas (quanto MENOR, melhor).
 * Na normalização: score = (1 - valor/pior) × 100
 */
export const INVERSE_METRICS = [
    'churnRate',
    'trialNoShowRate',
    'cancellations',
];

/**
 * Tipos de período para os snapshots.
 */
export const PERIOD_TYPES = {
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
    YEARLY: 'yearly',
};

export const PERIOD_TYPE_LABELS = {
    [PERIOD_TYPES.MONTHLY]: 'Mensal',
    [PERIOD_TYPES.QUARTERLY]: 'Trimestral',
    [PERIOD_TYPES.YEARLY]: 'Anual',
};

export const PERIOD_TYPE_OPTIONS = getOptionsFromMap(PERIOD_TYPES, PERIOD_TYPE_LABELS);

